import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CompanyDetail } from './CompanyDetail';
import { PersonalDetail } from './PersonalDetail';
import { downloadCOCDocumentAsync, getFreelancerAsync, updateFreelancerAsync } from '@application/features/freelancer/freelancerThunks';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { WelcomMsg } from './WelcomMsg';
import { useForm } from 'react-hook-form';
import { PrimaryButon } from '@ui/components/common/button/PrimaryButon';
import { convertToPdfFile } from '@application/common/helpers/file-helper';
import { translate } from '@application/common/helpers/translate';
import { checkShuftiProStatusAsync } from '@application/features/auth/authThunks';
import { formatYMD } from '@infrastructure/helpers';
import dayjs from 'dayjs';
import { EditInfoStepper } from './stepper';
import { getCountriesAsync } from '@application/features/country/countryThunk';
export const AccountInformation = () => {
    const theme = useTheme();
    const { freelancer } = useAppSelector((state) => state.freelancer);
    const { isLoading } = useAppSelector((state) => state.loading);
    const { isShuftiProVerified } = useAppSelector((state) => state.auth);
    const { shufftiProRedirectData } = useAppSelector((state) => state.shufftiPro);
    const [open, setOpen] = useState(false);
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState(['Personal Information']);
    const isMdUp = theme.breakpoints.up('md');
    const { user } = useAppSelector((state) => state.user);
    const { control, handleSubmit, formState: { errors }, trigger, setValue, setError, watch } = useForm({
        mode: 'all'
    });
    useEffect(() => {
        if (shufftiProRedirectData) {
            window.location.href = shufftiProRedirectData.verificationUrl;
        }
    }, [dispatch, shufftiProRedirectData]);
    useEffect(() => {
        dispatch(getFreelancerAsync());
        dispatch(getCountriesAsync());
        dispatch(checkShuftiProStatusAsync());
        if (!isShuftiProVerified) {
            setOpen(true);
        }
        else {
            setOpen(false);
        }
    }, [dispatch, searchParams, isShuftiProVerified, navigate]);
    useEffect(() => {
        if (freelancer) {
            const fieldsToUpdate = {
                countryId: freelancer.countryId || '',
                dateOfBirth: freelancer.dateOfBirth ? formatYMD(dayjs(freelancer.dateOfBirth).toDate()) : '',
                documentNumber: freelancer.documentNumber || '',
                firstName: freelancer.firstName || '',
                lastName: freelancer.lastName || '',
                email: freelancer.email || '',
                phoneNumber: freelancer.phoneNumber || '',
                Position: freelancer.position || '',
                address: freelancer.address || '',
                zipCode: freelancer.zipCode || '',
                companyName: freelancer.companyName || '',
                bankAccountTitle: freelancer.bankAccountTitle || '',
                companyAddress: freelancer.companyAddress || '',
                companyZipCode: freelancer.companyZipCode || '',
                KVK: freelancer.kvk || '',
                companyCountry: freelancer.companyCountry || '',
                vat: freelancer.vat || '',
                bankAccountNumber: freelancer.bankAccountNumber || '',
                status: freelancer.status,
                ContractType: freelancer.contractType || '',
                resourceNumber: freelancer.resourceNumber,
                projectTaskNumber: freelancer.projectTaskNumber,
                projectNumber: freelancer.projectNumber
            };
            Object.entries(fieldsToUpdate).forEach(([key, value]) => {
                setValue(key, value);
            });
            if (freelancer.contractType && freelancer.contractType !== '') {
                if (freelancer.contractType === 'Seconded') {
                    setSteps(['Personal Information']);
                }
                else {
                    setSteps(['Personal Information']);
                    //show only 1 step else do below
                    // setSteps(['Personal Information', 'Company Information']);
                }
            }
            else {
                if (freelancer.qtsPlacementType && freelancer.qtsPlacementType === 'Secondment') {
                    setSteps(['Personal Information']);
                }
                else {
                    setSteps(['Personal Information']);
                    //show only 1 step else do below
                    //setSteps(['Personal Information', 'Company Information']);
                }
            }
        }
    }, [freelancer, setValue]);
    const onSubmit = (updateFreelancer) => {
        const maxFileSize = 1048576;
        if (freelancer.document && (updateFreelancer.COCDocument === undefined || updateFreelancer.COCDocument === null)) {
            const fileDto = { folderName: freelancer?.document.folderName, fileName: freelancer?.document?.name };
            dispatch(downloadCOCDocumentAsync(fileDto))
                .unwrap()
                .then((res) => {
                // Convert to PDF and ensure the blob is actually a PDF
                const blob = convertToPdfFile(res.file);
                // Ensure the file name ends with ".pdf"
                let fileName = freelancer.document?.name || 'document';
                if (!fileName.toLowerCase().endsWith('.pdf')) {
                    fileName = fileName.replace(/\.[^/.]+$/, '') + '.pdf'; // Strip any existing extension and add .pdf
                }
                // Create the File object with the correct MIME type (force to application/pdf if not)
                const file = new File([blob], fileName, { type: 'application/pdf' });
                updateFreelancer.COCDocument = file;
                return dispatch(updateFreelancerAsync(updateFreelancer)).unwrap();
            })
                .then(() => {
                navigate('/my-profile');
            })
                .catch((error) => { });
            return;
        }
        if (updateFreelancer.COCDocument && updateFreelancer.COCDocument.size > maxFileSize) {
            setError('COCDocument', {
                message: translate('validations.maxAllowedFileSize')
            });
        }
        else {
            dispatch(updateFreelancerAsync(updateFreelancer))
                .unwrap()
                .then(() => {
                navigate('/my-profile');
            })
                .catch((error) => { });
        }
    };
    const handleNext = () => {
        trigger().then((isValid) => {
            if (isValid) {
                if (activeStep < steps.length - 1) {
                    setActiveStep((prevStep) => prevStep + 1);
                }
            }
        });
    };
    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prevStep) => prevStep - 1);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Grid, { container: true, columnSpacing: 4, height: isMdUp ? 'calc(100svh - 128px)' : '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: { xs: '25px 25px 50px 25px', md: '50px 50px 50px 50px' }, overflow: 'auto', children: [activeStep === 0 && (_jsxs(Grid, { spacing: 2, container: true, item: true, xs: 12, md: 6, overflow: 'hidden', children: [_jsxs(Box, { width: '100%', position: 'sticky', top: '-25px', sx: {
                                    background: '#F4F6F8',
                                    zIndex: 2
                                }, children: [_jsx(Box, { display: 'flex', justifyContent: 'center', width: '100%', children: _jsx(Typography, { fontSize: '28px', fontWeight: 500, color: '#000000', textAlign: 'center', children: "Enter your personal details here." }) }), _jsx(EditInfoStepper, { trigger: trigger, activeStep: activeStep, steps: steps, setActiveStep: setActiveStep })] }), _jsx(Grid, { container: true, columnSpacing: 2, pt: { xs: 2, md: 0 }, alignItems: 'center', spacing: 1, children: _jsx(PersonalDetail, { control: control, errors: errors }) }), _jsx(Grid, { item: true, xs: 12, sm: 6, md: 6, display: 'flex', justifyContent: 'flex-end', ml: 'auto', children: _jsx(PrimaryButon, { type: 'submit', onClick: handleSubmit(onSubmit), children: "Save" }) })] })), activeStep === 1 && (_jsxs(Grid, { rowGap: 2, container: true, item: true, xs: 12, md: 6, overflow: 'hidden', children: [_jsxs(Box, { width: '100%', position: 'sticky', top: '-25px', sx: {
                                    background: '#F4F6F8',
                                    zIndex: 2
                                }, children: [_jsx(Box, { display: 'flex', justifyContent: 'center', width: '100%', children: _jsx(Typography, { fontSize: '28px', fontWeight: 500, color: '#000000', textAlign: 'center', children: "Enter your company details here." }) }), _jsx(EditInfoStepper, { trigger: trigger, activeStep: activeStep, steps: steps, setActiveStep: setActiveStep })] }), _jsx(Grid, { container: true, columnSpacing: 2, pt: { xs: 2, md: 0 }, alignItems: 'center', spacing: 1, children: _jsx(CompanyDetail, { trigger: trigger, control: control, errors: errors }) }), _jsxs(Grid, { container: true, gap: 0.5, children: [_jsx(Grid, { item: true, xs: 12, sm: 5.9, md: 5.9, children: _jsx(PrimaryButon, { onClick: handleBack, children: "Back" }) }), _jsx(Grid, { item: true, xs: 12, sm: 5.9, md: 5.9, children: _jsx(PrimaryButon, { type: 'submit', onClick: handleSubmit(onSubmit), children: "Save" }) })] })] }))] }), isShuftiProVerified != undefined && _jsx(WelcomMsg, { open: !isShuftiProVerified })] }));
};
